import { useCallback, useState } from 'react';

import { COUNT_DEFAULT, PAGE_DEFAULT } from '../../../../../../shared/projectsV2';
import { getLocatorsParam, isMinimalProject } from '../shared/utils';
import type { MinimalProject, Project, ProjectsFilter } from '../../../../../../shared/projectsV2';
import type { AddedProject } from '../../../../../shared/branches/types';
import type { ProjectGroupReleaseProject } from '../../../../../types';
import type {
  AddedProjectIndex,
  ProjectSelection,
  ReleaseGroupProjectSort,
  SetProjectsFilter,
} from '../shared/types';

import useBulkActionToasts from './useBulkActionToasts';
import useProjects from './useProjects';
/**
 * Parameters to tweak which validation clauses are checked.
 */
export type ValidateParams = {
  /**
   * If creating a new release group, ensure that it has a name.
   */
  isCreatingGroup?: boolean;
};

// This will used by the three containers:
//   1. Create Group Container
//   2. Create Release Container
//   3. Edit Release Container
export type ReleaseGroupState = {
  /**
   * Name (title) of the current group.
   */
  name: string;
  /**
   * Set the name (title) of the current group.
   */
  setName: (name: string) => void;

  /**
   * Version (title) of the current release.
   */
  version: string;
  /**
   * Set the version (title) of the current release.
   */
  setVersion: (version: string) => void;

  /**
   * Tracks and indexes "added" projects by their locator.
   */
  addedProjects: AddedProjectIndex;
  /**
   * Add one or more projects to the current release.
   */
  addProjects: (projects: Project[]) => void;
  /**
   * Add one or more minimal projects to the current release.
   */
  addMinimalProjects: (projects: MinimalProject[]) => void;
  /**
   * Add one or more release group projects to the current release.
   */
  addReleaseGroupProjects: (projects: ProjectGroupReleaseProject[]) => void;
  /**
   * Remove one or more projects from the current release.
   */
  removeProjects: (projects: Project[] | MinimalProject[]) => void;
  /**
   * Update the branch, revision, and latestRevision values for an added project.
   */
  updateAddedProject: (project: Omit<AddedProject, 'teams'>) => void;

  /**
   * This value indicates whether the projects table should only show projects
   * that currently "added" and pending addition to the current release. In most
   * cases, this defaults to true. Only when creating a new release group from
   * scratch does it default to false.
   */
  shouldShowOnlyAddedProjects: boolean;
  /**
   * Update whether the projects should be only showing added projects or ALL
   * projects that match the current filters.
   */
  setShouldShowOnlyAddedProjects: (show: boolean) => void;

  /**
   * Takes a list of projects which are currently selected (where 'all' means on all pages),
   * and updates the revision of each project to be the latest revision.
   * Skips projects which are selected but aren't in addedProjects.
   * Skips projects which do not have a latestRevision.
   */
  selectLatestRevisionForSelectedProjects: (selectedProjects: ProjectSelection) => void;

  /**
   * Determines what projects are shown in the projects table.
   */
  filters: ProjectsFilter;
  /**
   * Updates what projects should be shown and fires off a refetch (of projects).
   */
  setFilters: SetProjectsFilter;

  /**
   * Validates that the state is valid and that the user is able to proceed with
   * the workflow. Returns one or more error messages if things are missing / invalid.
   */
  validate: (params?: ValidateParams) => string[];

  /**
   * The current page of projects being viewed.
   */
  page: number;
  /**
   * Set the current page of projects being viewed.
   */
  setPage: (page: number) => void;
  /**
   * The number of projects shown per page.
   */
  count: number;
  /**
   * Set the number of projects shown per page.
   */
  setCount: (count: number) => void;

  /**
   * Currently-loaded page of ProjectsV2 projects.
   */
  projects: Project[];
  /**
   * Number of projects that match the current filters.
   */
  projectCount: number;
  /**
   * Whether or not projects are currently being loaded.
   */
  areProjectsLoading: boolean;
  /**
   * Any errors that occurred while fetching projects.
   */
  projectErrors: string[] | null;

  /**
   * State for managing the sorting of projects in the projects table.
   */
  sort: ReleaseGroupProjectSort;
  /**
   * This return type is what is returned by useState
   */
  setSort: (sort: ReleaseGroupProjectSort) => void;
};

const useReleaseGroupState = (): ReleaseGroupState => {
  // Track the group's name and version state as strings.
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [shouldShowOnlyAddedProjects, setShouldShowOnlyAddedProjectsInternal] = useState(true);
  const [filters, setFilters] = useState<ProjectsFilter>({});
  const [addedProjects, setAddedProjects] = useState<AddedProjectIndex>({});
  const [sort, setSort] = useState<ReleaseGroupProjectSort>('title_asc');
  const [page, setPage] = useState(PAGE_DEFAULT);
  const [count, setCount] = useState(COUNT_DEFAULT);
  const toasts = useBulkActionToasts();

  /**
   * Whenever filters change, reset the current page back to 1.
   */
  const setFiltersInternal = (updatedFilters: ProjectsFilter) => {
    setPage(PAGE_DEFAULT);
    setFilters(updatedFilters);
  };

  /**
   * Adds projects to the current release.
   * If they have already been added, they will be updated with the
   * values provided in the projects array
   */
  const addProjects = (projects: Project[]) =>
    setAddedProjects(prevAddedProjects => {
      const counts = {
        added: 0,
        skipped: 0,
      };
      //  Clone it so we can mutate it
      const nextAddedProjects: AddedProjectIndex = { ...prevAddedProjects };
      projects.forEach((project) => {
        // Filter out any projects without a latest revision because they
        // cannot be added to releases. In the UI, projects without latest
        // revisions will be ineligible for addition.
        if (!project.latestRevision) {
          counts.skipped++;
          return;
        }
        const newProject: AddedProject = {
          locator: project.id,
          branch: project.branch,
          revision: project.latestRevision,
          latestRevision: project.latestRevision,
          teams: project.teams ?? [],
        };

        if (!nextAddedProjects[project.id]) {
          counts.added++;
        }

        nextAddedProjects[project.id] = newProject;
      });

      //  The projects[0] check is mostly just to appease TypeScript
      if (projects.length === 1 && counts.added === 1 && projects[0]) {
        toasts.addProject(projects[0].id);
      } else {
        toasts.addManyProjects(counts);
      }

      return nextAddedProjects;
    });

  /**
   * This is similar to addProjects, except it takes an array
   * of minimal projects instead of full projects.
   * Memoized because it is passed to a useEffect hook,
   * and because it is isolated from the rest of the state.
   */
  const addMinimalProjects = useCallback((projects: MinimalProject[]) =>
    setAddedProjects(prevAddedProjects => {
      const counts = {
        added: 0,
        skipped: 0,
      };
      //  Clone it so we can mutate it
      const nextAddedProjects: AddedProjectIndex = { ...prevAddedProjects };
      projects.forEach((project) => {
        // Filter out any minimal projects without a latest revision because they
        // cannot be added to releases.
        if (!project.latestRevision) {
          counts.skipped++;
          return;
        }
        const newProject: AddedProject = {
          locator: project.locator,
          branch: project.defaultBranch,
          revision: project.latestRevision,
          latestRevision: project.latestRevision,
          teams: project.teams ?? [],
        };

        if (!nextAddedProjects[project.locator]) {
          counts.added++;
        }

        nextAddedProjects[project.locator] = newProject;
      });

      //  The projects[0] check is mostly just to appease TypeScript
      if (projects.length === 1 && counts.added === 1 && projects[0]) {
        toasts.addProject(projects[0].locator);
      } else {
        toasts.addManyProjects(counts);
      }

      return nextAddedProjects;
    }), [toasts]);

  /**
   * This is similar to addProjects, except it takes an array of release group
   * projects instead of ProjectsV2 projects. Memoized because it is passed to
   * a useEffect hook and because it is isolated from the rest of the state.
   */
  const addReleaseGroupProjects = useCallback((projects: ProjectGroupReleaseProject[]) =>
    setAddedProjects((prevAddedProjects) => {
      const counts = {
        added: 0,
        skipped: 0,
      };
      //  Clone it so we can mutate it
      const nextAddedProjects: AddedProjectIndex = { ...prevAddedProjects };
      projects.forEach((project) => {
        if (!project.revision) {
          counts.skipped++;
          return;
        }
        const newProject: AddedProject = {
          locator: project.projectId,
          branch: project.branch,
          revision: {
            locator: project.revision.locator,
            message: project.revision.message,
          },
          teams: project.project.teams ?? [],
        };

        // For release group projects, their latestRevision is NOT necessarily
        // the same as the revision "snapshotted" in the release.
        const latestRevision = project.latestRevision;
        if (latestRevision) {
          newProject.latestRevision = {
            locator: latestRevision.locator,
            message: latestRevision.message,
          };
        }

        if (!nextAddedProjects[project.projectId]) {
          counts.added++;
        }

        nextAddedProjects[newProject.locator] = newProject;
      });

      if (projects.length === 1 && counts.added === 1 && projects[0]) {
        toasts.addProject(projects[0].projectId);
      } else {
        toasts.addManyProjects(counts);
      }

      return nextAddedProjects;
    }), [toasts]);

  /**
   * Removes a set of projects from the list of added projects.
   * You can pass it a list of Projects or MinimalProjects.
   */
  const removeProjects = (projects: Array<Project | MinimalProject>) =>
    setAddedProjects(prevAddedProjects => {
      const prevProjectsMap = new Map(Object.entries(prevAddedProjects));
      const removedProjects = projects.filter((project) => {
        if (isMinimalProject(project)) {
          return prevProjectsMap.delete(project.locator);
        }
        return prevProjectsMap.delete(project.id);
      });

      if (projects.length === 1 && removedProjects.length === 1 && projects[0]) {
        toasts.removeProject(projects[0]?.locator ?? projects[0]?.id);
      } else if (removedProjects.length > 0) {
        toasts.removeManyProjects(removedProjects.length);
      }

      return Object.fromEntries(prevProjectsMap);
    });

  const updateAddedProject = (project: Omit<AddedProject, 'teams'>) => {
    setAddedProjects((prevAddedProjects) => {
      // Clone the current addedProjects index so that we can mutate it.
      const nextAddedProjects: AddedProjectIndex = { ...prevAddedProjects };

      // This method should only ever be called on projects that are already
      // added so this check is mainly here to narrow the addedProject type.
      const addedProject = addedProjects[project.locator];
      if (!addedProject) {
        return nextAddedProjects;
      }

      addedProject.branch = project.branch;
      addedProject.revision = project.revision;
      addedProject.latestRevision = project.latestRevision;

      nextAddedProjects[project.locator] = addedProject;
      return nextAddedProjects;
    });
  };

  const validate = (params?: ValidateParams): string[] => {
    const validationErrors: string[] = [];

    if (params?.isCreatingGroup && name.length === 0) {
      validationErrors.push('Group name must not be empty.');
    }
    if (version.length === 0) {
      validationErrors.push('Release version must not be empty.');
    }
    if (Object.values(addedProjects).some((project) => !project.revision)) {
      validationErrors.push('All added projects must have a revision selected.');
    }

    return validationErrors;
  };

  const {
    projects,
    projectCount,
    isLoading: areProjectsLoading,
    projectErrors,
  } = useProjects(filters, sort, page, count);

  const setShouldShowOnlyAddedProjects = (showOnlyAddedProjects: boolean) => {
    setShouldShowOnlyAddedProjectsInternal(showOnlyAddedProjects);

    const locators = getLocatorsParam(showOnlyAddedProjects, addedProjects);
    setFiltersInternal({
      ...filters,
      locators,
    });
  };

  //  This function takes the selection of projects, iterates through the list of currently added projects,
  //  and for each one which was in the selection, it updates the `revision` to be `latestRevision`.
  const selectLatestRevisionForSelectedProjects = (selectedProjects: ProjectSelection) =>
    setAddedProjects(prevAddedProjects => {
      const counts = {
        updated: 0,
        skipped: 0,
      };
      //  Clone it so we can mutate it
      const nextAddedProjects = { ...prevAddedProjects };

      //  Handle all selection case
      if (selectedProjects === 'all') {
        //  If all projects are selected, update the revision for all projects.
        Object.entries(nextAddedProjects).forEach(([locator, project]) => {
          if (project.latestRevision) {
            counts.updated++;
            nextAddedProjects[locator] = { ...project, revision: project.latestRevision };
          } else {
            counts.skipped++;
          }
        });
        toasts.setLatestRevision(counts);
        return nextAddedProjects;
      }

      selectedProjects.forEach((projectLocator) => {
        const project = nextAddedProjects[projectLocator];
        if (!project) {
          return;
        }
        if (project.latestRevision) {
          counts.updated++;
          nextAddedProjects[projectLocator] = { ...project, revision: project.latestRevision };
        } else {
          counts.skipped++;
        }
      });

      toasts.setLatestRevision(counts);
      return nextAddedProjects;
    });

  // When changing the page size, reset to the first page of results.
  const setCountInternal = (newCount: number) => {
    setCount(newCount);
    setPage(PAGE_DEFAULT);
  };

  return {
    name,
    setName,
    version,
    setVersion,
    addedProjects,
    addProjects,
    addMinimalProjects,
    addReleaseGroupProjects,
    removeProjects,
    updateAddedProject,
    shouldShowOnlyAddedProjects,
    setShouldShowOnlyAddedProjects,
    filters,
    setFilters: setFiltersInternal,
    validate,
    page,
    setPage,
    count,
    setCount: setCountInternal,
    projects,
    projectCount,
    areProjectsLoading,
    projectErrors,
    selectLatestRevisionForSelectedProjects,
    sort,
    setSort,
  };
};

export default useReleaseGroupState;
